<script setup lang="ts">
import { MMIcon, MMText } from '@mm-frontend/mm-web-ui-kit';
import type { CategoryMM } from '~/entities/category';
import { useCategoriesTracker } from '~/features/categories';

const props = defineProps<{
  category: CategoryMM;
  isPath?: boolean;
  isLastPathItem?: boolean;
  isPromo?: boolean;
}>();

const showIcon = computed(() => {
  const icon = props.category.icon.link;
  return (props.isPath && props.isLastPathItem && !!icon) || (!props.isPath && !!icon);
});

const hasChildrens = computed(() => {
  return props.category.childrens?.length;
});

const isLink = computed(() => props.isPromo || props.isLastPathItem || (!hasChildrens.value && props.category.id !== 0));

const component = computed(() => {
  if (isLink.value) return defineNuxtLink({});
  return 'div';
});

const { trackCategoryClick } = useCategoriesTracker();
function onClick() {
  if (!isLink.value) return;
  trackCategoryClick(props.category);
}
</script>

<template>
  <component :is="component" :to="category.url" class="category" @click="onClick">
    <div class="category__icon">
      <img v-if="showIcon" :src="category.icon.link" />
      <MMIcon v-else-if="isPath" name="chevron" :angle="90" :width="18" :height="18" />
    </div>

    <MMText
      class="category__title"
      :font="isPromo ? 'Body M/Medium' : 'Body M/Regular'"
      :color="isLastPathItem ? 'accented' : 'primary'"
    >
      {{ category.title }}
    </MMText>

    <div class="category__icon">
      <MMIcon
        v-if="!isPath"
        name="chevron"
        :angle="hasChildrens ? 0 : -90"
        :width="18"
        :height="18"
      />
    </div>
  </component>
</template>

<style scoped lang="stylus">
.category
  display flex
  align-items center
  justify-content space-between
  gap 16px
  text-decoration none
  color inherit

  &__title
    flex 1

  &__icon
    width 24px
    height 24px
    flex-shrink 0
    display flex
    align-items center
    justify-content center

    & img
      width 100%
      height 100%
      border-radius 50%
      object-fit cover
</style>
